import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { submitEnquiry } from "@/lib/api/enquiries.functions";
import { INDIA_STATES, lookupPincode } from "@/lib/india-states";

const I_AM = ["Organizer", "Club Secretary", "Individual", "Politician", "Govt Official", "Others"] as const;
const PROGRAM_TYPES = [
  "Wedding Ceremony", "Reception", "Club Program", "Cultural Program",
  "Corporate Event", "Political Event", "Government Event",
  "Private Function", "Musical Night", "Others",
] as const;

const schema = z.object({
  name: z.string().trim().min(2, "Name required").max(100),
  email: z.string().trim().email("Invalid email").max(255),
  mobile: z.string().regex(/^[6-9]\d{9}$/, "Enter 10-digit mobile"),
  event_date: z.string().optional().or(z.literal("")),
  program_type: z.enum(PROGRAM_TYPES),
  pincode: z.string().regex(/^\d{6}$/, "6-digit pincode"),
  state: z.string().min(2, "State required"),
  district: z.string().min(2, "District required"),
  city: z.string().min(1, "City required"),
  area: z.string().min(1, "Area required").max(200),
  i_am: z.enum(I_AM),
  message: z.string().max(1000).optional().or(z.literal("")),
});

type FormData = z.infer<typeof schema>;

const STEP_FIELDS: Record<number, (keyof FormData)[]> = {
  1: ["name", "mobile", "email"],
  2: ["event_date", "program_type"],
  3: ["pincode", "state", "district", "city", "area"],
  4: ["i_am"],
  5: ["message"],
};

const STEP_LABELS = ["Contact", "Event", "Location", "About You", "Message"];

export function EnquiryForm({ compact = false }: { compact?: boolean }) {
  const submit = useServerFn(submitEnquiry);
  const [step, setStep] = useState(1);
  const [districts, setDistricts] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [loadingPin, setLoadingPin] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: "onTouched",
    defaultValues: {
      name: "", email: "", mobile: "", event_date: "",
      program_type: "Cultural Program",
      pincode: "", state: "", district: "", city: "", area: "",
      i_am: "Individual", message: "",
    },
  });

  const onPincodeBlur = async (pin: string) => {
    if (!/^\d{6}$/.test(pin)) return;
    setLoadingPin(true);
    const res = await lookupPincode(pin);
    setLoadingPin(false);
    if (res) {
      form.setValue("state", res.state, { shouldValidate: true });
      setDistricts([res.district]);
      form.setValue("district", res.district, { shouldValidate: true });
      setCities(res.cities);
      if (res.cities[0]) form.setValue("city", res.cities[0], { shouldValidate: true });
    } else {
      toast.error("Could not look up pincode. Please fill location manually.");
    }
  };

  const next = async () => {
    const valid = await form.trigger(STEP_FIELDS[step]);
    if (valid) setStep((s) => Math.min(5, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const onSubmit = async (data: FormData) => {
    try {
      await submit({ data });
      toast.success("Enquiry sent! We'll reach out shortly.");
      form.reset();
      setStep(1);
      setDistricts([]); setCities([]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to submit");
    }
  };

  const grid = compact ? "grid gap-3 sm:grid-cols-2" : "grid gap-4 sm:grid-cols-2";

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      {/* Step indicator */}
      <div className="flex items-center justify-between gap-1 sm:gap-2">
        {STEP_LABELS.map((label, i) => {
          const n = i + 1;
          const done = step > n;
          const active = step === n;
          return (
            <div key={label} className="flex flex-1 flex-col items-center gap-1">
              <div
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold transition ${
                  done ? "bg-primary text-primary-foreground"
                    : active ? "bg-primary/90 text-primary-foreground ring-2 ring-primary/30 ring-offset-2 ring-offset-background"
                    : "bg-secondary text-muted-foreground"
                }`}
              >
                {done ? <Check className="h-3.5 w-3.5" /> : n}
              </div>
              <span className={`hidden text-[10px] uppercase tracking-wider sm:block ${active ? "text-primary" : "text-muted-foreground"}`}>
                {label}
              </span>
            </div>
          );
        })}
      </div>

      <div className="min-h-[220px]">
        {step === 1 && (
          <div className={grid}>
            <Field label="Full Name" className="sm:col-span-2">
              <Input {...form.register("name")} placeholder="Full name" />
              <Err msg={form.formState.errors.name?.message} />
            </Field>
            <Field label="Mobile Number">
              <Input {...form.register("mobile")} placeholder="10-digit mobile" inputMode="numeric" maxLength={10} />
              <Err msg={form.formState.errors.mobile?.message} />
            </Field>
            <Field label="Email Address">
              <Input type="email" {...form.register("email")} placeholder="you@example.com" />
              <Err msg={form.formState.errors.email?.message} />
            </Field>
          </div>
        )}

        {step === 2 && (
          <div className={grid}>
            <Field label="Event Date">
              <Input type="date" {...form.register("event_date")} min={new Date().toISOString().slice(0, 10)} />
            </Field>
            <Field label="Program Type">
              <Select
                value={form.watch("program_type")}
                onValueChange={(v) => form.setValue("program_type", v as typeof PROGRAM_TYPES[number], { shouldValidate: true })}
              >
                <SelectTrigger><SelectValue placeholder="Select program type" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {PROGRAM_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              <Err msg={form.formState.errors.program_type?.message} />
            </Field>
          </div>
        )}

        {step === 3 && (
          <div className={grid}>
            <Field label="Pincode">
              <Input
                {...form.register("pincode")}
                placeholder="e.g. 700001"
                inputMode="numeric"
                maxLength={6}
                onBlur={(e) => onPincodeBlur(e.target.value)}
              />
              {loadingPin && <p className="mt-1 text-xs text-muted-foreground">Looking up…</p>}
              <Err msg={form.formState.errors.pincode?.message} />
            </Field>
            <Field label="State">
              <Select value={form.watch("state")} onValueChange={(v) => form.setValue("state", v, { shouldValidate: true })}>
                <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {INDIA_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Err msg={form.formState.errors.state?.message} />
            </Field>
            <Field label="District">
              {districts.length > 0 ? (
                <Select value={form.watch("district")} onValueChange={(v) => form.setValue("district", v, { shouldValidate: true })}>
                  <SelectTrigger><SelectValue placeholder="Select district" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {districts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input {...form.register("district")} placeholder="District" />
              )}
              <Err msg={form.formState.errors.district?.message} />
            </Field>
            <Field label="City / Post Office">
              {cities.length > 0 ? (
                <Select value={form.watch("city")} onValueChange={(v) => form.setValue("city", v, { shouldValidate: true })}>
                  <SelectTrigger><SelectValue placeholder="Select city" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input {...form.register("city")} placeholder="City" />
              )}
              <Err msg={form.formState.errors.city?.message} />
            </Field>
            <Field label="Area Name" className="sm:col-span-2">
              <Input {...form.register("area")} placeholder="Locality, landmark, street" />
              <Err msg={form.formState.errors.area?.message} />
            </Field>
          </div>
        )}

        {step === 4 && (
          <div className={grid}>
            <Field label="I Am" className="sm:col-span-2">
              <Select value={form.watch("i_am")} onValueChange={(v) => form.setValue("i_am", v as typeof I_AM[number], { shouldValidate: true })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {I_AM.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
        )}

        {step === 5 && (
          <div className="grid gap-4">
            <Field label="Additional Message (Optional)">
              <Textarea
                rows={5}
                {...form.register("message")}
                placeholder='e.g. "Need a live performance for a cultural program on 15 August."'
              />
              <Err msg={form.formState.errors.message?.message} />
            </Field>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 pt-2">
        <Button type="button" variant="outline" onClick={back} disabled={step === 1}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Back
        </Button>
        {step < 5 ? (
          <Button type="button" onClick={next} className="bg-primary text-primary-foreground hover:bg-primary/90">
            Next <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        ) : (
          <Button type="submit" disabled={form.formState.isSubmitting} className="bg-primary text-primary-foreground hover:bg-primary/90">
            {form.formState.isSubmitting ? "Sending…" : "Send Enquiry"}
          </Button>
        )}
      </div>
    </form>
  );
}

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      <Label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
function Err({ msg }: { msg?: string }) {
  if (!msg) return null;
  return <p className="mt-1 text-xs text-destructive">{msg}</p>;
}
